import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { debounce } from 'lodash';
import { Layout, Page, Box, InlineStack, BlockStack, Text, Button, useIndexResourceState } from '@shopify/polaris';
import { useDispatch, useSelector } from 'react-redux';
import { getAllRule, handleResetRule } from '../redux/rule/slice';
import { deleteRuleAsync, getRuleAsync } from '../redux/rule';
import useOnboardingRedirect from '../redux/userOnBoardingRedirect';
import { getAllUserOnboarding } from '../redux/userOnboarding/slice';
import RuleTable from '../components/rule/RuleTable';
import { limit } from '../utils';
import ModalComponent from '../components/common/Modal';

const RulesPage = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { userOnboardingData } = useSelector(getAllUserOnboarding);
    const { rules, totalCount, isRuleLoading, isDeleteRuleLoading } = useSelector(getAllRule);
    const [searchValue, setSearchValue] = useState('');
    const [selected, setSelected] = useState(0);
    const [page, setPage] = useState(1);
    const [sortSelected, setSortSelected] = useState(['createdAt asc']);
    const [isDeleteLoading, setIsDeleteLoading] = useState(false);
    const [isModalOpen, setIsModalOpen] = useState(false);

    useOnboardingRedirect(userOnboardingData);

    const { selectedResources, allResourcesSelected, handleSelectionChange, clearSelection } = useIndexResourceState(rules, {
        resourceIDResolver: (rule) => rule._id
    });

    const debouncedSearch = useMemo(
        () =>
            debounce(async (pagination) => {
                dispatch(getRuleAsync({
                    payload: pagination
                }));
            }, 300),
        [dispatch]
    );

    useEffect(() => {
        handlePaginationChange({
            page: page,
            status: selected === 0 ? 'all' : selected === 1 ? 'active' : 'inactive',
            search: searchValue,
            sort: sortSelected,
            limit: limit
        });
    }, [selected, searchValue, page, sortSelected, isDeleteLoading]);

    const handlePaginationChange = (pagination) => {
        debouncedSearch(pagination);
    };

    const handleFiltersQueryChange = useCallback(
        (value) => setSearchValue(value),
        [],
    );

    const handleQueryValueRemove = useCallback(() => setSearchValue(''), []);

    const handleNavigate = () => {
        dispatch(handleResetRule());
        navigate('/create-rule');
    };

    const handleEditRule = (e, id) => {
        e.stopPropagation();
        dispatch(handleResetRule());
        navigate(`/update-rule/${id}`);
    };

    const handleDeleteRule = (e) => {
        e.stopPropagation();
        dispatch(deleteRuleAsync({
            payload: { ids: selectedResources },
            callback: () => {
                setIsModalOpen(false);
                clearSelection();
                setIsDeleteLoading(!isDeleteLoading);
            }
        }));
    };

    const handleModalOpen = useCallback(() => setIsModalOpen(!isModalOpen), [isModalOpen]);

    return (
        <Page>
            <Layout>
                <Layout.Section>
                    <Box padding='200'>
                        <InlineStack align='space-between' blockAlign='center'>
                            <BlockStack gap='100'>
                                <Text variant='headingMd'>Rules Overview</Text>
                                <Text variant='bodySm' tone='subdued'>
                                    Manage your rules.
                                </Text>
                            </BlockStack>
                            <Button
                                variant='primary'
                                onClick={handleNavigate}
                            >
                                Create Rule
                            </Button>
                        </InlineStack>
                    </Box>
                </Layout.Section>
                <Layout.Section>
                    <RuleTable
                        rules={rules}
                        totalRulesCount={totalCount}
                        onPaginationChange={handlePaginationChange}
                        isRuleLoading={isRuleLoading}
                        handleFiltersQueryChange={handleFiltersQueryChange}
                        handleQueryValueRemove={handleQueryValueRemove}
                        searchValue={searchValue}
                        selected={selected}
                        setSelected={setSelected}
                        sortSelected={sortSelected}
                        setSortSelected={setSortSelected}
                        page={page}
                        setPage={setPage}
                        handleEdit={handleEditRule}
                        handleDelete={handleModalOpen}
                        selectedResources={selectedResources}
                        allResourcesSelected={allResourcesSelected}
                        handleSelectionChange={handleSelectionChange}
                    />
                </Layout.Section>
            </Layout>
            <ModalComponent
                open={isModalOpen}
                titleHidden={false}
                title="Delete Rule"
                primaryAction={{
                    content: 'Delete',
                    destructive: true,
                    loading: isDeleteRuleLoading,
                    onAction: (e) => handleDeleteRule(e)
                }}
                secondaryActions={[]}
                handleModalClose={handleModalOpen}
            >
                <Text as='p'>
                    The rule{selectedResources.length > 1 ? 's you selected have' : ' you selected has'} been marked for deletion. Are you sure you want to proceed?
                </Text>
            </ModalComponent>
        </Page>
    );
};

export default RulesPage;